import { Provider } from 'react-redux';
import './config/aws-config';
import { store } from './store';

import { useEffect } from 'react';
import { Route, Routes } from 'react-router-dom';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import SessionTimeoutModal from './components/SessionTimeoutModal';
import SessionWarningModal from './components/SessionWarningModal';
import { AuthProvider } from './contexts/AuthContext';
import { SessionProvider, useSession } from './contexts/SessionContext';
import { setSessionModalTrigger } from './services/instance';

// Login Screens
import Login from './components/Auth/Login';
import LoginOTP from './components/Auth/LoginOTP';
import OtpVerification from './components/Auth/OtpVerification';
import Welcome from './components/Auth/Welcome';

import Dashboard from './components/Dashboard';

import CreateAdminUsers from './components/SystemAdminUsers/AdminUsers/CreateAdminUsers';
import SystemAdminUsers from './components/SystemAdminUsers/SystemAdminUsers';
import UserManagement from './components/UserManagement/UserManagement';

import CreateRole from './components/RolesAndPermission/CreateRole/CreateRole';
import RolesAndPermissions from './components/RolesAndPermission/RolesAndPermissions';

import CreateProject from './components/Projects/CreateProject/CreateProject';
import Projects from './components/Projects/Projects';

import CreateSurvey from './components/SurveyManagement/CreateSurvey/CreateSurvey';
import SurveyManagement from './components/SurveyManagement/SurveyManagement';

import CreateSetSurvey from './components/SurveySetManagement/CreateSurveySet/CreateSurveySet';
import SelectedSurveySet from './components/SurveySetManagement/SelectedSurveySet';
import SurveySetManagement from './components/SurveySetManagement/SurveySetManagement';

import SiteManagement from './components/SiteManagement/SiteManagement';

import CreateNotification from './components/NotificationManagement/CreateNotification/CreateNotification';
import NotificationManagement from './components/NotificationManagement/NotificationManagement';

import WalletManagement from './components/WalletManagement/WalletManagement';

import FAQs from './components/FAQs/FAQs';

import { Toaster } from 'react-hot-toast';
import AuthorizedRoute from './components/authguards/AuthorizedRoute';
import CreateFaqForm from './components/FAQs/CreateFaqForm';
import EditFaqForm from './components/FAQs/EditFaqForm';
import DashboardLayout from './components/Layout/DashboardLayout';
import GlobalLayout from './components/Layout/GlobalLayout';
import NotFoundPage from './components/NotFoundPage';
import EditProjectSite from './components/Projects/EditSite';
import ProjectView from './components/Projects/IndividualProject';
import UserManagementProject from './components/Projects/UserManagement';
import SessionTimeout from './components/SessionTimeout';
import AllSitesPage from './components/SiteManagement/AllSitesPage';
import EditSite from './components/SiteManagement/EditView';
import ProjectViewPage from './components/SiteManagement/ProjectView';
import SystemProjectUsers from './components/SystemAdminUsers/SystemProjectUser';
import Unauthorized from './components/Unauthorized';

const AppContent = () => {
  const {
    showSessionModal,
    setShowSessionModal,
    showWarningModal,
    setShowWarningModal,
    extendSession,
  } = useSession();

  useEffect(() => {
    setSessionModalTrigger(() => setShowSessionModal(true));
  }, [setShowSessionModal]);

  const handleWarningLogout = async () => {
    setShowWarningModal(false);
    setShowSessionModal(true);
  };

  return (
    <>
      <AuthProvider>
        <Routes>
          <Route path="/" element={<Login />} />
          <Route path="/login/otp" element={<LoginOTP />} />
          <Route path="/verify" element={<OtpVerification />} />
          <Route path="/welcome" element={<Welcome />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            {/* Dashboard */}
            <Route index element={<Dashboard />} />

            {/* UserManagement */}
            <Route path="users-management" element={<UserManagement />} />

            {/* System Admin Users */}
            <Route path="system-admin-users" element={<SystemAdminUsers />} />
            <Route path="system-project-users" element={<SystemProjectUsers />} />
            <Route element={<AuthorizedRoute action="write" resource="users" />}>
              <Route path="system-admin-users/create" element={<CreateAdminUsers />} />
            </Route>
            <Route element={<AuthorizedRoute action="edit" resource="users" />}>
              <Route path="system-admin-users/edit" element={<CreateAdminUsers />} />
            </Route>

            {/* Roles and Permissions */}
            <Route path="roles-and-permissions" element={<RolesAndPermissions />}></Route>
            <Route path="roles-and-permissions/create" element={<CreateRole />} />

            {/* Projects */}
            <Route path="project-management" element={<Projects />} />
            <Route element={<AuthorizedRoute action="write" resource="projects" />}>
              <Route path="project-management/create" element={<CreateProject />} />
              <Route path="project-management/edit" element={<CreateProject />} />
            </Route>

            <Route path="project-management/IndividualProject" element={<ProjectView />} />
            <Route element={<AuthorizedRoute action="write" resource="sites" />}>
              <Route path="project-management/EditProjectSite" element={<EditProjectSite />} />
            </Route>

            <Route path="project-management/user-management" element={<UserManagementProject />} />

            {/* Survey Management */}

            <Route path={`survey-management`} element={<SurveyManagement />} />
            <Route element={<AuthorizedRoute action="write" resource="surveys" />}>
              <Route path="survey-management/create" element={<CreateSurvey />} />
              <Route path={`survey-management/edit`} element={<CreateSurvey />} />
            </Route>

            {/* SurveySet Management */}
            <Route path={`surveyset-management`} element={<SurveySetManagement />} />
            <Route element={<AuthorizedRoute action="write" resource="surveySets" />}>
              <Route path="surveyset-management/create" element={<CreateSetSurvey />} />
            </Route>
            <Route element={<AuthorizedRoute action="edit" resource="surveys" />}>
              <Route path="surveyset-management/survey/edit" element={<CreateSurvey />} />
            </Route>
            <Route element={<AuthorizedRoute action="edit" resource="surveySets" />}>
              <Route path="surveyset-management/edit" element={<CreateSetSurvey />} />
            </Route>

            <Route path="surveyset-management/selected-surveyset" element={<SelectedSurveySet />} />

            {/* Site Management*/}
            <Route path="site-management" element={<SiteManagement />} />
            <Route path="site-management/ProjectViewPage" element={<ProjectViewPage />} />
            {/* <Route element={<AuthorizedRoute action="write" resource="sites" />}> */}
            <Route path="site-management/EditSite" element={<EditSite />} />
            {/* </Route> */}
            <Route path="site-management/all-sites" element={<AllSitesPage />} />

            {/* Notification Management */}
            <Route path="notification-management" element={<NotificationManagement />} />
            <Route element={<AuthorizedRoute action="write" resource="sites" />}>
              <Route path="notification-management/create" element={<CreateNotification />} />
            </Route>

            {/* wallet */}
            <Route path="wallet-management" element={<WalletManagement />} />

            {/* FAQs */}
            <Route path="faqs" element={<FAQs />} />
            <Route element={<AuthorizedRoute action="write" resource="faqs" />}>
              <Route path="faqs/create" element={<CreateFaqForm />} />
              <Route path="faqs/edit/:id" element={<EditFaqForm />} />
            </Route>
          </Route>
          <Route path="/session-timeout" element={<SessionTimeout />} />
          <Route path="/not-found" element={<NotFoundPage />} />
          <Route path="/unauthorized" element={<Unauthorized />} />
        </Routes>
      </AuthProvider>
      <SessionTimeoutModal isOpen={showSessionModal} onClose={() => setShowSessionModal(false)} />
      <SessionWarningModal
        isOpen={showWarningModal}
        onExtend={extendSession}
        onLogout={handleWarningLogout}
      />
    </>
  );
};

const App = () => {
  return (
    <ErrorBoundary>
      <Provider store={store}>
        <GlobalLayout>
          <Toaster position="top-center" />
          <SessionProvider>
            <AppContent />
          </SessionProvider>
        </GlobalLayout>
      </Provider>
    </ErrorBoundary>
  );
};

export default App;
